import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocationStore } from '@/store/location';
import { naverMapService } from '@/services/naverMap';
import { MapStoreCard } from '@/components/map/MapStoreCard';
import { formatDistance } from '@/lib/distance';
import { checkOrderAvailability, type OrderType } from '@/lib/operatingHours';
import { type RestaurantWithProduct } from '@/types';


interface MobileMapViewProps {
  restaurants: RestaurantWithProduct[];
  selectedOrderType: OrderType;
}

declare global {
  interface Window {
    naver: any;
  }
}

export function MobileMapView({ restaurants, selectedOrderType }: MobileMapViewProps) {
  const navigate = useNavigate();
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const userLocationMarkerRef = useRef<any>(null);
  const restaurantMarkersRef = useRef<any[]>([]);
  const [, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(false);
  const [isMapLoading, setIsMapLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState<RestaurantWithProduct | null>(null);
  const { currentLocation } = useLocationStore();

  // 네이버 지도 초기화
  useEffect(() => {
    const initMap = async () => {
      if (!mapRef.current) return;

      try {
        setMapReady(false); // 재초기화 시작 시 false로 명시적 리셋
        setIsMapLoading(true);
        
        // 초기 중심 위치 설정
        let initialCenter;
        if (currentLocation?.coordinate && 
            currentLocation.coordinate.latitude !== 0 && 
            currentLocation.coordinate.longitude !== 0) {
          // 사용자가 설정한 주소가 있으면 그 위치를 중심으로
          initialCenter = {
            latitude: currentLocation.coordinate.latitude,
            longitude: currentLocation.coordinate.longitude
          };
        } else {
          // 기본 위치 (서울 시청)
          initialCenter = {
            latitude: 37.5665,
            longitude: 126.9780
          };
        }

        // naverMapService를 통한 지도 생성
        const map = await naverMapService.createMap('mobile-map-container', {
          center: initialCenter,
          zoom: 15,
          zoomControl: true
        });
        
        mapInstanceRef.current = map;
        setIsMapLoading(false);
        setMapReady(true);
        
        // 지도 로딩 완료 후 React state를 통해 마커 생성이 자동으로 트리거됨
      } catch (error) {
        console.error('MobileMapView 지도 초기화 실패:', error);
        
        // 오류 시 사용자에게 안내
        if (mapRef.current) {
          const errorMessage = (error as Error).message.includes('타임아웃') 
            ? '네트워크 연결을 확인하고 새로고침해주세요.'
            : '지도 서비스에 일시적인 문제가 발생했습니다.';
            
          mapRef.current.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center; height: 100%; background-color: #f5f5f5; flex-direction: column; padding: 20px; text-align: center;">
              <div style="margin-bottom: 16px; font-size: 18px; font-weight: bold; color: #666;">지도를 불러올 수 없습니다</div>
              <div style="font-size: 14px; color: #999; line-height: 1.5; margin-bottom: 16px;">
                ${errorMessage}
              </div>
              <button onclick="window.location.reload()" style="
                background: #4CD964; 
                color: white; 
                border: none; 
                padding: 8px 16px; 
                border-radius: 8px; 
                font-size: 14px; 
                cursor: pointer;
              ">
                새로고침
              </button>
            </div>
          `;
        }
        setIsMapLoading(false);
      }
    };

    initMap();
  }, [currentLocation]);

  // 사용자 위치 요청
  const requestUserLocation = () => {
    if (!navigator.geolocation) {
      // Geolocation is not supported
      return;
    }

    setIsLoadingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const location = { lat: latitude, lng: longitude };
        setUserLocation(location);
        
        // 지도 중심을 사용자 위치로 이동
        if (mapInstanceRef.current && naverMapService.isMapLoaded()) {
          naverMapService.setMapCenter(
            mapInstanceRef.current, 
            { latitude, longitude },
            true
          );
          mapInstanceRef.current.setZoom(16);
          
          // 사용자 위치 마커 추가
          addUserLocationMarker(location);
        }
        
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('위치 권한 거부 또는 오류:', error);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };

  // 사용자 위치 마커 추가
  const addUserLocationMarker = (location: { lat: number; lng: number }) => {
    if (!mapInstanceRef.current || !naverMapService.isMapLoaded()) return;

    // 기존 마커 제거
    if (userLocationMarkerRef.current) {
      userLocationMarkerRef.current.setMap(null);
    }

    // naverMapService를 통한 사용자 위치 마커 생성
    userLocationMarkerRef.current = naverMapService.createMarker(
      mapInstanceRef.current,
      { latitude: location.lat, longitude: location.lng },
      {
        icon: {
          content: `
            <div style="
              width: 20px; 
              height: 20px; 
              background-color: #1D2DE5; 
              border: 2px solid white; 
              border-radius: 50%; 
              box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            "></div>
          `,
          anchor: new window.naver.maps.Point(10, 10)
        }
      }
    );
  };

  // 주문 가능 여부 확인 함수 (헬퍼 함수 사용)
  const isOrderAvailable = (restaurant: RestaurantWithProduct): boolean => {
    return checkOrderAvailability(restaurant, selectedOrderType);
  };

  // 매장 마커 추가
  useEffect(() => {
    // 지도가 준비되지 않았거나 매장 데이터가 없으면 return
    if (!mapReady || !mapInstanceRef.current || restaurants.length === 0) {
      return;
    }

    const visibleRestaurants = restaurants.filter(r => (r as any).is_test_store !== true);

    // 기존 마커 제거
    restaurantMarkersRef.current.forEach(marker => {
      marker.setMap(null);
    });
    restaurantMarkersRef.current = [];

    // 매장 마커 추가 (모든 매장 표시, 주문 불가능한 매장은 회색으로)
    visibleRestaurants.forEach((restaurant) => {
      // 좌표가 없는 매장은 스킵
      if (!restaurant.latitude || !restaurant.longitude) {
        return;
      }
      
      const isAvailable = isOrderAvailable(restaurant);
      const markerColor = isAvailable ? '#00E676' : '#999999';
      
      // leaf_marker.svg를 사용한 마커 생성 (크기 증가)
      const marker = naverMapService.createMarker(
        mapInstanceRef.current,
        { latitude: restaurant.latitude, longitude: restaurant.longitude },
        {
          icon: {
            content: `
              <div style="
                width: 40px; 
                height: 40px; 
                display: flex;
                align-items: center;
                justify-content: center;
                filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
              ">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <g clip-path="url(#clip0_4_24093_${restaurant.id})">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M17.11 3.00613C17.2594 3.02258 17.4031 3.07248 17.5305 3.15212C17.6579 3.23176 17.7658 3.33909 17.846 3.46613C21.046 8.53313 20.563 13.9121 17.89 17.3001C16.555 18.9911 14.68 20.1711 12.493 20.5201C10.585 20.8231 8.5 20.4841 6.399 19.3841C6.206 19.9931 6.069 20.5841 5.99 21.1411C5.95157 21.4028 5.81106 21.6386 5.59921 21.7969C5.38736 21.9552 5.12143 22.0231 4.85961 21.9858C4.5978 21.9485 4.36141 21.809 4.20218 21.5979C4.04295 21.3867 3.97385 21.1211 4.01 20.8591C4.14856 19.9311 4.38384 19.0202 4.712 18.1411C3.175 16.4511 2.755 14.3131 2.942 12.4031C3.148 10.2961 4.109 8.27513 5.36 7.23213C7.032 5.83913 8.788 5.26713 10.312 4.98713C10.978 4.87167 11.6486 4.78459 12.322 4.72613C12.801 4.67813 13.29 4.64613 13.758 4.53013C14.4506 4.36217 15.1126 4.08684 15.72 3.71413C16.162 3.43813 16.539 2.94213 17.11 3.00613ZM7.15 17.5181C8.262 15.2511 10.08 13.0781 12.447 11.8941C12.5655 11.8361 12.6714 11.7551 12.7584 11.6559C12.8455 11.5567 12.9121 11.4413 12.9543 11.3163C12.9966 11.1912 13.0137 11.0591 13.0046 10.9274C12.9955 10.7958 12.9605 10.6672 12.9015 10.5491C12.8425 10.4311 12.7607 10.3259 12.6609 10.2396C12.561 10.1533 12.4451 10.0876 12.3197 10.0463C12.1944 10.005 12.0621 9.98898 11.9305 9.99907C11.7989 10.0092 11.6706 10.0452 11.553 10.1051C8.945 11.4091 6.947 13.6641 5.648 16.0691C5.90619 16.4898 6.22648 16.8691 6.598 17.1941C6.783 17.3101 6.967 17.4171 7.15 17.5181Z" fill="${markerColor}"/>
                  </g>
                  <defs>
                    <clipPath id="clip0_4_24093_${restaurant.id}">
                      <rect width="24" height="24" fill="white"/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            `,
            anchor: new window.naver.maps.Point(20, 32)
          }
        }
      );

      // 클릭 이벤트 추가 - 매장 카드 표시
      window.naver.maps.Event.addListener(marker, 'click', () => {
        setSelectedRestaurant(restaurant as RestaurantWithProduct);
      });

      restaurantMarkersRef.current.push(marker);
    });
  }, [restaurants, selectedOrderType, mapReady]);

  // 현 위치로 설정하기 버튼 클릭
  const handleSetCurrentLocation = () => {
    requestUserLocation();
  };

  return (
    <div className="relative w-full h-full">
      {/* 지도 컨테이너 */}
      <div id="mobile-map-container" ref={mapRef} className="w-full h-full" />
      
      {/* 지도 로딩 상태 */}
      {isMapLoading && (
        <div className="absolute inset-0 bg-white bg-opacity-90 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-600">지도를 불러오는 중...</p>
          </div>
        </div>
      )}
      
      {/* 현 위치로 설정하기 버튼 */}
      <button
        onClick={handleSetCurrentLocation}
        className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-[#4CD964] text-white font-semibold px-3 py-2 rounded-lg shadow-lg flex items-center gap-1.5 hover:bg-[#3FC754] transition-colors"
        style={{ minWidth: '140px', whiteSpace: 'nowrap' }}
      >
        <img src="/icons/icon_gps.svg" alt="GPS" className="w-5 h-5 filter brightness-0 invert flex-shrink-0" />
        <span className="text-[14px] font-semibold">현 위치로 설정하기</span>
      </button>

      {/* 로딩 중 표시 */}
      {isLoadingLocation && (
        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 bg-white px-4 py-2 rounded-lg shadow-md">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-gray-700">위치 확인 중...</span>
          </div>
        </div>
      )}
      
      {/* 선택된 매장 카드 */}
      {selectedRestaurant && (
        <div className="absolute top-8 left-4 right-4 z-[1000] flex justify-center">
          <div className="relative">
            {/* 닫기 버튼 */}
            <button
              onClick={() => setSelectedRestaurant(null)}
              className="absolute -top-2 -right-2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors z-10 border border-gray-200"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M1 1L15 15M1 15L15 1"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
            
            {/* 매장 카드 */}
            <MapStoreCard
              id={selectedRestaurant.id}
              name={selectedRestaurant.name}
              bestmenu={selectedRestaurant.bestmenu}
              pickupInstructions={selectedRestaurant.pickup_instructions}
              products={selectedRestaurant.products?.map((product: any) => ({
                id: product.id,
                name: product.name,
                original_price: product.original_price,
                image_url: product.image_url,
                offers: product.offers || [],
                current_stock: product.stock?.[0]?.current_stock || 0,
                is_available: product.is_available ?? true
              }))}
              isOpen={selectedRestaurant.isOrderAvailable ?? selectedRestaurant.is_open ?? false}
              isNew={selectedRestaurant.is_new}
              storeType={selectedRestaurant.store_type}
              imageUrl={selectedRestaurant.image_url}
              distance={selectedRestaurant.distance ? formatDistance(selectedRestaurant.distance) : undefined}
              selectedOrderType={selectedOrderType}
              deadlineDiscountStartTime={selectedRestaurant.deadline_discount_start_time}
              deadlineDiscountEndTime={selectedRestaurant.deadline_discount_end_time}
              deadline_discount_enabled={selectedRestaurant.deadline_discount_enabled}
              // Operating hours will be handled by helper functions in MapStoreCard
              onCardClick={() => navigate(`/store/${selectedRestaurant.id}?orderType=${selectedOrderType}`)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
